// Client-side workflow evaluation against a work order's configured workflow:
// guard evaluation, lane derivation, and the local seed the stub pages still
// read. Status codes are workflow configuration throughout - there is no
// separate presentation vocabulary to translate to or from.

import { missingRequiredFields, requiredFieldsOf } from '@atlas/core-utils/work-order'
import { isChangeRequestResolved } from '@/domain/change-requests'
import { checklistLabel } from '@/domain/checklist-stub'
import { REQUIRED_FIELD_LABEL } from '@/domain/required-fields'
import type { BoardStatus, WorkOrder } from '@/domain/work-order.types'
import type {
	ChangeRequest,
	ChecklistState,
	ResolvedWorkflowSnapshot,
	ResolvedWorkspace,
	TransitionAttemptResult,
	TransitionGuard,
	ValidationLane,
} from '@/domain/workflow.types'
import { HookCode, WorkspaceCode } from '@/domain/workflow.types'
import { DEFAULT_WORKFLOW_ID, DEFAULT_WORKFLOW_STATUSES, TRANSITIONS_BY_STATUS } from '@/domain/workflow-stub.seed'
import { resolveWorkspaceCodeFor } from '@/domain/workspace-assignment-stub'

/**
 * Whether the work order's assigned workflow actually defines `code` as an
 * active status - the check a CTA needs before it can submit a transition
 * target read off a snapshot that may since have gone stale. `statuses` must be
 * the ASSIGNED workflow's own active statuses; a board page's workflow list
 * covers only the orders it happened to return and is not a substitute.
 */
export function workflowDefinesStatus(code: string, statuses: readonly BoardStatus[]): boolean {
	return statuses.some((status) => status.code === code)
}

/**
 * Derive the validation lane from a configured status code - never a stored
 * field, so it can never drift out of sync with the status. Mirrors the
 * backend's own `resolveLane`; a workflow whose validation statuses are named
 * something else simply has no lane, which is a legitimate configuration.
 */
export function resolveLane(statusCode: string): ValidationLane | null {
	if (statusCode === 'internal_review' || statusCode === 'internal_validation') return 'internal'
	if (statusCode === 'design_validation') return 'design'
	if (statusCode === 'external_review' || statusCode === 'external_validation') return 'compliance'
	if (statusCode === 'review_source_material') return 'source_material'
	return null
}

/** The `GET /work-orders/{id}` workflow-snapshot fields the state timeline and action bar read. */
/**
 * The resolved workspace + its `options` for one status, given the work
 * order's own asset type. This stub's `options` are illustrative
 * placeholders, not a pinned-down schema — the real per-kind shape still
 * needs to be decided component by component.
 */
function resolveWorkspaceForStatus(statusId: string, assetTypeCode: string | null): ResolvedWorkspace {
	const code = resolveWorkspaceCodeFor(statusId, assetTypeCode)
	const options: Record<string, unknown> =
		code === WorkspaceCode.VALIDATION
			? { lane: resolveLane(statusId) }
			: code === WorkspaceCode.PRODUCTION
				? { requiredSlots: ['visual'] }
				: {}
	return { code, options }
}

export function resolveWorkflowSnapshot(workOrder: WorkOrder): ResolvedWorkflowSnapshot {
	const currentStatusId = workOrder.status
	return {
		workflowId: DEFAULT_WORKFLOW_ID,
		// Per-status `workspace` is resolved here, not baked into the static seed —
		// it depends on the WORK ORDER's own assetTypeCode (an override can differ
		// order to order), mirroring the real backend's per-(status, assetType)
		// resolution.
		statuses: DEFAULT_WORKFLOW_STATUSES.map((status) => ({
			...status,
			workspace: resolveWorkspaceForStatus(status.id, workOrder.assetTypeCode),
		})),
		currentStatusId,
		legalTransitions: TRANSITIONS_BY_STATUS[currentStatusId] ?? [],
		lane: resolveLane(currentStatusId),
	}
}

/**
 * A change request has no separate confirmation step — checking every item off
 * IS resolution — so the evaluators need nothing but its items. Kept
 * structural rather than the full `ChangeRequest` so a caller (and a test
 * fixture) can hand over just the items.
 */
type ChangeRequestStateLike = Pick<ChangeRequest, 'items'>

/** What `evaluateGuard`/`attemptTransition` read to decide pass/fail — assembled by the caller from persisted and stub state. */
export interface GuardContext {
	/** Keyed by `checklistKey` param (e.g. 'previo', 'production'). */
	checklists?: Record<string, ChecklistState | undefined>
	/** Keyed by lane — every change request raised on that lane. A lane with none is omitted rather than present-and-empty. */
	changeRequests?: Partial<Record<ValidationLane, ChangeRequestStateLike[]>>
	/** Keyed by artifact name. */
	artifacts?: Record<string, boolean>
	/** Keyed by slot name (e.g. 'visual'). */
	deliverySlots?: Record<string, boolean>
}

interface GuardEvaluation {
	passed: boolean
	message: string
}

const pass: GuardEvaluation = { passed: true, message: '' }
const fail = (message: string): GuardEvaluation => ({ passed: false, message })

function evaluateAssigneePresent(workOrder: WorkOrder): GuardEvaluation {
	return workOrder.assigneeIds.length > 0 ? pass : fail('Needs an assignee before it can be picked up.')
}

function evaluateFieldsPresent(guard: TransitionGuard, workOrder: WorkOrder): GuardEvaluation {
	const missing = missingRequiredFields(workOrder, requiredFieldsOf(guard.params ?? {}))
	if (missing.length === 0) return pass
	return fail(`Missing ${missing.map((field) => REQUIRED_FIELD_LABEL[field].toLowerCase()).join(', ')}.`)
}

function evaluateChecklistComplete(guard: TransitionGuard, ctx: GuardContext): GuardEvaluation {
	const key = String(guard.params?.checklistKey ?? '')
	return ctx.checklists?.[key]?.confirmed ? pass : fail(`The ${checklistLabel(key)} isn't complete yet.`)
}

function evaluateDeliverySlotsFilled(guard: TransitionGuard, ctx: GuardContext): GuardEvaluation {
	const slot = String(guard.params?.slot ?? '')
	return ctx.deliverySlots?.[slot] ? pass : fail(`The "${slot}" delivery slot is still empty.`)
}

function evaluateArtifactExists(guard: TransitionGuard, ctx: GuardContext): GuardEvaluation {
	const artifact = String(guard.params?.artifact ?? '')
	return ctx.artifacts?.[artifact] ? pass : fail(`"${artifact}" hasn't been generated yet.`)
}

/** A guard's lane: its own `lane` param when configured, otherwise the lane of the status the order sits on. */
const guardLane = (guard: TransitionGuard, workOrder: WorkOrder): ValidationLane | null =>
	(guard.params?.lane as ValidationLane | undefined) ?? resolveLane(workOrder.status)

/**
 * Needs an OPEN request on the lane, matching the backend: a resolved one from
 * an earlier round is a record, not a reason to send the order back again.
 */
function evaluateChangeRequestCreated(
	guard: TransitionGuard,
	workOrder: WorkOrder,
	ctx: GuardContext,
): GuardEvaluation {
	const lane = guardLane(guard, workOrder)
	const hasOpen = !!lane && (ctx.changeRequests?.[lane] ?? []).some((cr) => !isChangeRequestResolved(cr))
	return hasOpen ? pass : fail('Raise a change request before sending it back.')
}

/**
 * Resolved once every change request on the lane has every item verified by
 * the reviewer — folded over the lane's whole list: one outstanding request
 * blocks the lane no matter how many others are done. The worker's done ticks
 * never resolve a request on their own.
 */
function evaluateChangeRequestResolved(
	guard: TransitionGuard,
	workOrder: WorkOrder,
	ctx: GuardContext,
): GuardEvaluation {
	const lane = guardLane(guard, workOrder)
	const laneRequests = lane ? ctx.changeRequests?.[lane] : undefined
	const allResolved = (laneRequests ?? []).every(isChangeRequestResolved)
	return allResolved ? pass : fail('There is an unresolved change request for this lane.')
}

const GUARD_EVALUATORS: Record<
	HookCode,
	(guard: TransitionGuard, workOrder: WorkOrder, ctx: GuardContext) => GuardEvaluation
> = {
	[HookCode.ASSIGNEE_PRESENT]: (_guard, workOrder) => evaluateAssigneePresent(workOrder),
	[HookCode.FIELDS_PRESENT]: evaluateFieldsPresent,
	[HookCode.CHECKLIST_COMPLETE]: (guard, _workOrder, ctx) => evaluateChecklistComplete(guard, ctx),
	[HookCode.DELIVERY_SLOTS_FILLED]: (guard, _workOrder, ctx) => evaluateDeliverySlotsFilled(guard, ctx),
	[HookCode.ARTIFACT_EXISTS]: (guard, _workOrder, ctx) => evaluateArtifactExists(guard, ctx),
	[HookCode.CHANGE_REQUEST_CREATED]: evaluateChangeRequestCreated,
	[HookCode.CHANGE_REQUEST_RESOLVED]: evaluateChangeRequestResolved,
	// Post-hooks never appear as guards (HookStage pairing rule) — nothing to evaluate if one somehow does.
	[HookCode.EMIT_EVENT]: () => pass,
	[HookCode.NOTIFY]: () => pass,
	[HookCode.LOG_MESSAGE]: () => pass,
}

/**
 * The guards a move is held to. A withdrawal shares the reviewer's send-back
 * edge on the seeded workflow but is the assignee's own move, and only a
 * reviewer can raise a change request, so it skips `change_request_created` —
 * as the backend does.
 */
export function guardsForMove(guards: readonly TransitionGuard[], move: { withdrawal: boolean }): TransitionGuard[] {
	return move.withdrawal ? guards.filter((guard) => guard.hookCode !== HookCode.CHANGE_REQUEST_CREATED) : [...guards]
}

/** Pass/fail + a human "what's missing" message for one guard, per the confirmed guard semantics. */
export function evaluateGuard(guard: TransitionGuard, workOrder: WorkOrder, ctx: GuardContext): GuardEvaluation {
	return GUARD_EVALUATORS[guard.hookCode](guard, workOrder, ctx)
}

/**
 * Evaluates every guard on an ALREADY-RESOLVED transition, in order, short-
 * circuiting on the first failure. This is the seam a caller holding a real
 * `WorkflowTransitionSnapshot` (from the real `GET /work-orders/{id}`
 * `workflowSnapshot`) uses, to say what is missing before the user tries. The
 * backend re-checks `checklist_complete`, `fields_present` and both change-request
 * guards on every transition; the other guards are enforced here only.
 */
export function evaluateTransitionGuards(
	guards: readonly TransitionGuard[],
	workOrder: WorkOrder,
	ctx: GuardContext,
): TransitionAttemptResult {
	for (const guard of guards) {
		const evaluation = evaluateGuard(guard, workOrder, ctx)
		if (!evaluation.passed) {
			return { ok: false, errorCode: 'GUARD_UNMET', hookCode: guard.hookCode, message: evaluation.message }
		}
	}
	return { ok: true }
}

/** The `POST /transitions` attempt this stub stands in for — looks up the transition from the local seed, then defers to {@link evaluateTransitionGuards}. */
export function attemptTransition(
	workOrder: WorkOrder,
	toStatusId: string,
	ctx: GuardContext = {},
): TransitionAttemptResult {
	const transition = (TRANSITIONS_BY_STATUS[workOrder.status] ?? []).find((t) => t.toStatusId === toStatusId)
	if (!transition) {
		return { ok: false, errorCode: 'ILLEGAL_TRANSITION', message: 'No such transition from the current status.' }
	}
	return evaluateTransitionGuards(transition.guards, workOrder, ctx)
}
